import { getProductCollectionV2 } from "@insite/client-framework/Services/ProductServiceV2";
import { getWishListLines } from "@insite/client-framework/Services/WishListService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { CartLineModel, CartModel, ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import { getSettingsCollection } from "@store/Framework/Store/Context/ContextSelectors";

type ProductLookup = {
    id?: string | null;
    erpNumber?: string | null;
    productNumber?: string | null;
    properties?: { [key: string]: string } | null;
};

export type ItemsInspectionLineLookup = {
    id?: string | null;
    productId?: string | null;
    erpNumber?: string | null;
    qtyOrdered?: number | null;
    properties?: { [key: string]: string } | null;
};

export interface ItemsInspectionRequirement {
    inspectionCode: string;
    quantity: number;
    productKey: string;
    productId?: string;
    erpNumber?: string;
}

export const itemsInspectionPickupLockedMessage =
    "You have pre-inspections in your cart so these items must be shipped and cannot be picked up.";

export const itemsInspectionPickupBlockedMessage =
    "This item requires pre-inspection and must be shipped. Remove pre-inspection items from the cart to use Pick Up fulfillment.";

export const itemsInspectionBulkModalHeadline = "Add shipping inspection";

export const itemsInspectionBulkModalMessage =
    "One or more items require inspection items for shipped orders. Add the required inspection items to the cart as well?";

export const itemsInspectionPropertyName = "ItemsInspectionCode";
const itemsInspectionProductsByCode: Record<string, Promise<ProductModel | undefined> | undefined> = {};

const normalizeValue = (value?: string | null) => value?.trim() || "";
const normalizeKey = (value?: string | null) => normalizeValue(value).toLowerCase();

const getPropertyValue = (
    properties?: { [key: string]: string } | null,
    propertyName?: string | null,
) => {
    const normalizedPropertyName = normalizeValue(propertyName).toLowerCase();
    if (!properties || !normalizedPropertyName) {
        return "";
    }

    const matchingKey = Object.keys(properties).find(key => key.trim().toLowerCase() === normalizedPropertyName);
    return normalizeValue(matchingKey ? properties[matchingKey] : "");
};

const getConfiguredItemsInspectionCode = (product?: Pick<ProductLookup, "properties"> | null) =>
    getPropertyValue(product?.properties, itemsInspectionPropertyName);

const getInspectionProductCode = (product?: Pick<ProductLookup | ItemsInspectionLineLookup, "erpNumber"> | null) =>
    normalizeValue(product?.erpNumber);

type InspectionLineCodeLookup = {
    id?: string | null;
    productId?: string | null;
    erpNumber?: string | null;
    properties?: { [key: string]: string } | null;
};

export type ItemsInspectionPromptDecision = "include" | "exclude";

const getLineProductId = (line?: ItemsInspectionLineLookup | ProductLookup | null) =>
    normalizeValue((line as ItemsInspectionLineLookup | undefined)?.productId || line?.id);

const isSameProduct = (
    product?: ProductLookup | null,
    line?: Pick<ItemsInspectionLineLookup, "productId" | "erpNumber"> | null,
) => {
    const productId = getLineProductId(product);
    const lineProductId = normalizeValue(line?.productId);
    if (productId && lineProductId) {
        return productId === lineProductId;
    }

    const productCode = normalizeKey(product?.erpNumber || product?.productNumber);
    const lineCode = normalizeKey(line?.erpNumber);
    return !!productCode && productCode === lineCode;
};

const getItemsInspectionRequirementProductKey = (product?: ItemsInspectionLineLookup | ProductLookup | null) =>
    getLineProductId(product) || normalizeValue(product?.erpNumber);

const getLineQuantity = (line?: Pick<ItemsInspectionLineLookup, "qtyOrdered"> | null) => {
    const quantity = Number(line?.qtyOrdered || 0);
    return Number.isFinite(quantity) ? quantity : 0;
};

export const getItemsInspectionCode = (product?: ProductLookup | null) => {
    const configuredInspectionCode = getConfiguredItemsInspectionCode(product);
    if (configuredInspectionCode) {
        return configuredInspectionCode;
    }

    return "";
};

export const isItemsInspectionWarehouseExcluded = (state: ApplicationState) => {
    const warehouseName = normalizeKey(
        (getCurrentCartState(state).value?.defaultWarehouse || getSession(state).pickUpWarehouse)?.name,
    );
    if (!warehouseName) {
        return false;
    }

    const exclusions = getSettingsCollection(state).extensionCatalogSettings?.preInspectionWarehouseExclusions;
    return (exclusions || "").split(",").some(warehouseCode => normalizeKey(warehouseCode) === warehouseName);
};

export const getItemsInspectionProduct = async (inspectionCode?: string | null) => {
    const normalizedInspectionCode = normalizeValue(inspectionCode);
    if (!normalizedInspectionCode) {
        return undefined;
    }

    if (!itemsInspectionProductsByCode[normalizedInspectionCode]) {
        const productPromise = (async () => {
            const result = await getProductCollectionV2({
                productNumbers: [normalizedInspectionCode],
                page: 1,
                pageSize: 1,
                expand: ["properties"],
            });

            return result.products?.find(
                product =>
                    normalizeValue(product.productNumber).toLowerCase() === normalizedInspectionCode.toLowerCase(),
            );
        })();

        itemsInspectionProductsByCode[normalizedInspectionCode] = productPromise.catch(error => {
            delete itemsInspectionProductsByCode[normalizedInspectionCode];
            throw error;
        });
    }

    return itemsInspectionProductsByCode[normalizedInspectionCode];
};

export const getItemsInspectionProductUnitOfMeasure = (product?: Pick<ProductModel, "unitOfMeasures"> | null) => {
    const defaultUnitOfMeasure = product?.unitOfMeasures?.find(unitOfMeasure => unitOfMeasure.isDefault)?.unitOfMeasure;
    return normalizeValue(defaultUnitOfMeasure) || normalizeValue(product?.unitOfMeasures?.[0]?.unitOfMeasure) || "EA";
};

const isSameLine = (line?: InspectionLineCodeLookup | null, otherLine?: InspectionLineCodeLookup | null) => {
    if (normalizeValue(line?.id) && normalizeValue(otherLine?.id)) {
        return normalizeValue(line?.id) === normalizeValue(otherLine?.id);
    }

    return (
        normalizeValue(line?.productId) === normalizeValue(otherLine?.productId) &&
        normalizeValue(line?.erpNumber) === normalizeValue(otherLine?.erpNumber)
    );
};

export const isAutoManagedItemsInspectionCartLine = (
    cartLine?: InspectionLineCodeLookup | null,
    cartLines?: InspectionLineCodeLookup[] | null,
) => !!getItemsInspectionCodeForCartLine(cartLine, cartLines);

export const getItemsInspectionCodeForCartLine = (
    cartLine?: InspectionLineCodeLookup | null,
    cartLines?: InspectionLineCodeLookup[] | null,
) => {
    const inspectionProductCode = getInspectionProductCode(cartLine);
    const normalizedInspectionProductCode = normalizeKey(inspectionProductCode);
    if (!normalizedInspectionProductCode || !cartLines?.length) {
        return "";
    }

    const hasSourceLine = cartLines.some(
        line => !isSameLine(line, cartLine) && normalizeKey(getItemsInspectionCode(line)) === normalizedInspectionProductCode,
    );

    return hasSourceLine ? inspectionProductCode : "";
};

export const getSourceCartLinesForManagedItemsInspectionLine = (
    cartLine?: ItemsInspectionLineLookup | null,
    cartLines?: ItemsInspectionLineLookup[] | null,
) => {
    const inspectionCode = getItemsInspectionCodeForCartLine(cartLine, cartLines);
    const normalizedInspectionCode = normalizeKey(inspectionCode);
    if (!normalizedInspectionCode || !cartLines?.length) {
        return [];
    }

    return cartLines.filter(
        line =>
            !isSameLine(line, cartLine) &&
            !isAutoManagedItemsInspectionCartLine(line, cartLines) &&
            getLineQuantity(line) > 0 &&
            normalizeKey(getItemsInspectionCode(line)) === normalizedInspectionCode,
    );
};

export const getItemsInspectionPromptDecisionForProduct = (
    product?: ProductLookup | null,
    cartLines?: ItemsInspectionLineLookup[] | null,
): ItemsInspectionPromptDecision | undefined => {
    const inspectionCode = normalizeKey(getItemsInspectionCode(product));
    if (!inspectionCode || !cartLines?.length) {
        return undefined;
    }

    const matchingProductLines = cartLines.filter(
        line =>
            !isAutoManagedItemsInspectionCartLine(line, cartLines) &&
            getLineQuantity(line) > 0 &&
            isSameProduct(product, line),
    );

    if (matchingProductLines.length === 0) {
        return undefined;
    }

    return matchingProductLines.some(line => normalizeKey(getItemsInspectionCode(line)) === inspectionCode)
        ? "include"
        : "exclude";
};

export const getManagedItemsInspectionCartLines = (cartLines?: CartLineModel[] | null) =>
    (cartLines || []).filter(cartLine => isAutoManagedItemsInspectionCartLine(cartLine, cartLines));

export const hasManagedItemsInspectionCartLines = (cartLines?: CartLineModel[] | null) =>
    getManagedItemsInspectionCartLines(cartLines).length > 0;

export const isItemsInspectionPickupSelectionLocked = (cart?: Pick<CartModel, "cartLines"> | null) =>
    hasManagedItemsInspectionCartLines(cart?.cartLines);

export const getItemsInspectionRequirementsForLines = (
    lines?: ItemsInspectionLineLookup[] | null,
) => {
    const requirementsByKey: Record<string, ItemsInspectionRequirement> = {};

    (lines || []).forEach(line => {
        if (isAutoManagedItemsInspectionCartLine(line, lines)) {
            return;
        }

        const quantity = getLineQuantity(line);
        if (quantity <= 0) {
            return;
        }

        const inspectionCode = getItemsInspectionCode({
            id: getLineProductId(line),
            erpNumber: line.erpNumber,
            properties: line.properties || undefined,
        });

        if (!inspectionCode) {
            return;
        }

        const productKey = getItemsInspectionRequirementProductKey(line);
        const requirementKey = `${inspectionCode}::${productKey}`;

        if (!requirementsByKey[requirementKey]) {
            requirementsByKey[requirementKey] = {
                inspectionCode,
                quantity: 0,
                productKey,
                productId: getLineProductId(line) || undefined,
                erpNumber: normalizeValue(line.erpNumber) || undefined,
            };
        }

        requirementsByKey[requirementKey].quantity += quantity;
    });

    return Object.values(requirementsByKey);
};

export const isItemsInspectionPickupBlockedForLines = (
    lines?: ItemsInspectionLineLookup[] | null,
    fulfillmentMethod?: string | null,
) => fulfillmentMethod === "PickUp" && getItemsInspectionRequirementsForLines(lines).length > 0;

export const loadAllWishListLinesForItemsInspection = async (
    wishList?: Pick<WishListModel, "id" | "wishListLineCollection" | "wishListLinesCount"> | null,
) => {
    if (!wishList?.id) {
        return [] as WishListLineModel[];
    }

    const existingLines = wishList.wishListLineCollection || [];
    if (existingLines.length >= (wishList.wishListLinesCount || 0)) {
        return existingLines;
    }

    const wishListLinesResult = await getWishListLines({
        wishListId: wishList.id,
        page: 1,
        pageSize: Math.max(wishList.wishListLinesCount || 0, existingLines.length, 1),
    });

    return wishListLinesResult.wishListLines || [];
};

export const getDesiredItemsInspectionQuantities = (
    cart?: Pick<CartModel, "id" | "fulfillmentMethod" | "cartLines"> | null,
) => {
    const desiredInspectionQuantities: Record<string, number> = {};

    if (!cart?.cartLines || cart.fulfillmentMethod !== "Ship") {
        return desiredInspectionQuantities;
    }

    cart.cartLines.forEach(cartLine => {
        if (
            isAutoManagedItemsInspectionCartLine(cartLine, cart.cartLines) ||
            !cartLine.qtyOrdered ||
            cartLine.qtyOrdered <= 0
        ) {
            return;
        }

        const inspectionCode = getItemsInspectionCode({
            id: cartLine.productId,
            erpNumber: cartLine.erpNumber,
            properties: cartLine.properties,
        });

        if (!inspectionCode) {
            return;
        }

        desiredInspectionQuantities[inspectionCode] = (desiredInspectionQuantities[inspectionCode] || 0) + cartLine.qtyOrdered;
    });

    return desiredInspectionQuantities;
};